import React, { useState, useMemo } from 'react';
import { Sparkles, Eye, EyeOff, AlertCircle, CheckCircle, Mail, Lock, UserCheck } from 'lucide-react';
import { supabase } from '../config/supabase';
import type { User } from '@supabase/supabase-js';
import type { PasswordStrength } from '../services/authService';
import {
  validateEmail,
  validatePassword,
  validateFullName,
  validatePasswordMatch,
  parseAuthError,
  getPasswordStrengthLabel,
  getPasswordStrengthColor,
  checkEmailExists
} from '../services/authService';

interface SignUpViewProps {
  onAuthSuccess: (user: User) => void;
  isDark: boolean;
  onShowLogin?: () => void;
}

const SignUpView: React.FC<SignUpViewProps> = ({ onAuthSuccess, isDark, onShowLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Validation states
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    password: false,
    confirmPassword: false
  });

  // Compute validations
  const fullNameValidation = useMemo(
    () => touched.fullName ? validateFullName(fullName) : { valid: true },
    [fullName, touched.fullName]
  );

  const emailValidation = useMemo(
    () => touched.email ? validateEmail(email) : { valid: true },
    [email, touched.email]
  );

  const passwordStrength: PasswordStrength = useMemo(
    () => touched.password ? validatePassword(password) : { score: 0, feedback: [], isValid: true },
    [password, touched.password]
  );

  const confirmPasswordValidation = useMemo(
    () => touched.confirmPassword ? validatePasswordMatch(password, confirmPassword) : { valid: true },
    [password, confirmPassword, touched.confirmPassword]
  );

  const isFormValid = 
    fullNameValidation.valid &&
    emailValidation.valid &&
    passwordStrength.isValid &&
    confirmPasswordValidation.valid &&
    agreeToTerms;

  const validateForm = (): boolean => {
    const newTouched = {
      fullName: true,
      email: true,
      password: true,
      confirmPassword: true
    };
    setTouched(newTouched);

    if (!fullNameValidation.valid) {
      setError('error' in fullNameValidation ? fullNameValidation.error : 'Invalid full name');
      return false;
    }
    if (!emailValidation.valid) {
      setError('error' in emailValidation ? emailValidation.error : 'Invalid email');
      return false;
    }
    if (!passwordStrength.isValid) {
      setError('Password does not meet security requirements');
      return false;
    }
    if (!confirmPasswordValidation.valid) {
      setError('error' in confirmPasswordValidation ? confirmPasswordValidation.error : 'Passwords do not match');
      return false;
    }
    if (!agreeToTerms) {
      setError('Please agree to the Terms of Service');
      return false;
    }

    return true;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Check if email already exists before attempting signup
      const emailAlreadyExists = await checkEmailExists(email.trim());
      if (emailAlreadyExists) {
        setError('This email is already registered. Please sign in instead.');
        setLoading(false);
        return;
      }

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            full_name: fullName.trim(),
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signUpError) {
        setError(parseAuthError(signUpError) || 'Failed to sign up. Please try again.');
      } else if (data.user) {
        setSuccess('✓ Account created! Check your email to verify.');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFullName('');
        setAgreeToTerms(false);
        
        // Auto-login if email is confirmed immediately
        if (data.user.confirmed_at) {
          setTimeout(() => {
            onAuthSuccess(data.user as User);
          }, 1000);
        }
      }
    } catch (err) {
      setError(parseAuthError(err) || 'Failed to sign up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 transition-colors duration-300 ${
      isDark
        ? 'bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950'
        : 'bg-gradient-to-br from-zinc-50 via-white to-zinc-50'
    }`}>
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-red-600 via-orange-600 to-rose-600 flex items-center justify-center shadow-lg ${
            isDark ? 'shadow-red-500/30' : 'shadow-red-300/40'
          }`}>
            <Sparkles className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent mb-2">
            Get Started
          </h2>
          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Create your account and begin learning
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-green-600 dark:text-green-400 font-medium">{success}</p>
              <p className="text-xs text-green-600/70 dark:text-green-400/70 mt-1">
                You can sign in once you verify your email.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Sign Up Form */}
        <form onSubmit={handleSignUp} className={`space-y-5 rounded-2xl shadow-xl p-8 mb-6 ${
          isDark 
            ? 'bg-zinc-900/50 border border-zinc-800' 
            : 'bg-white/80 border border-zinc-200'
        }`}>
          {/* Full Name Input */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
              Full Name
            </label>
            <div className="relative">
              <UserCheck className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                fullNameValidation.valid === false ? 'text-red-500' : isDark ? 'text-zinc-500' : 'text-zinc-400'
              }`} />
              <input
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={() => setTouched({ ...touched, fullName: true })}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-colors ${
                  fullNameValidation.valid === false
                    ? 'border-red-500 focus:border-red-600'
                    : isDark
                    ? 'border-zinc-700 bg-zinc-800 focus:border-red-500'
                    : 'border-zinc-300 bg-white focus:border-red-500'
                } focus:outline-none focus:ring-2 focus:ring-red-500/20`}
                disabled={loading}
              />
            </div>
            {fullNameValidation.valid === false && 'error' in fullNameValidation && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {fullNameValidation.error}
              </p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
              Email Address
            </label>
            <div className="relative">
              <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                emailValidation.valid === false ? 'text-red-500' : isDark ? 'text-zinc-500' : 'text-zinc-400'
              }`} />
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched({ ...touched, email: true })}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-colors ${
                  emailValidation.valid === false
                    ? 'border-red-500 focus:border-red-600'
                    : isDark
                    ? 'border-zinc-700 bg-zinc-800 focus:border-red-500'
                    : 'border-zinc-300 bg-white focus:border-red-500'
                } focus:outline-none focus:ring-2 focus:ring-red-500/20`}
                disabled={loading}
              />
            </div>
            {emailValidation.valid === false && 'error' in emailValidation && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {emailValidation.error}
              </p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
              Password
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                passwordStrength.isValid === false && touched.password ? 'text-red-500' : isDark ? 'text-zinc-500' : 'text-zinc-400'
              }`} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched({ ...touched, password: true })}
                className={`w-full pl-10 pr-12 py-3 rounded-lg border-2 transition-colors ${
                  passwordStrength.isValid === false && touched.password
                    ? 'border-red-500 focus:border-red-600'
                    : isDark
                    ? 'border-zinc-700 bg-zinc-800 focus:border-red-500'
                    : 'border-zinc-300 bg-white focus:border-red-500'
                } focus:outline-none focus:ring-2 focus:ring-red-500/20`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                  isDark
                    ? 'text-zinc-400 hover:text-zinc-300'
                    : 'text-zinc-400 hover:text-zinc-600'
                }`}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {touched.password && password && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${getPasswordStrengthColor(passwordStrength.score)}`}
                      style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-zinc-400">
                    {getPasswordStrengthLabel(passwordStrength.score)}
                  </span>
                </div>
                {passwordStrength.feedback.length > 0 && (
                  <ul className="space-y-1">
                    {passwordStrength.feedback.map((tip, i) => (
                      <li key={i} className={`text-xs ${
                        tip.startsWith('✓')
                          ? 'text-green-500'
                          : 'text-zinc-400'
                      }`}>
                        {tip.startsWith('✓') ? '✓' : '•'} {tip.replace('✓ ', '')}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Confirm Password Input */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
              Confirm Password
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                confirmPasswordValidation.valid === false && touched.confirmPassword ? 'text-red-500' : isDark ? 'text-zinc-500' : 'text-zinc-400'
              }`} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouched({ ...touched, confirmPassword: true })}
                className={`w-full pl-10 pr-12 py-3 rounded-lg border-2 transition-colors ${
                  confirmPasswordValidation.valid === false && touched.confirmPassword
                    ? 'border-red-500 focus:border-red-600'
                    : isDark
                    ? 'border-zinc-700 bg-zinc-800 focus:border-red-500'
                    : 'border-zinc-300 bg-white focus:border-red-500'
                } focus:outline-none focus:ring-2 focus:ring-red-500/20`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
                className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                  isDark
                    ? 'text-zinc-400 hover:text-zinc-300'
                    : 'text-zinc-400 hover:text-zinc-600'
                }`}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmPasswordValidation.valid === false && touched.confirmPassword && 'error' in confirmPasswordValidation && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {confirmPasswordValidation.error}
              </p>
            )}
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-start gap-3 pt-2">
            <input
              type="checkbox"
              id="terms"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 mt-1 rounded border-zinc-300 accent-red-600 cursor-pointer flex-shrink-0"
            />
            <label htmlFor="terms" className={`text-sm cursor-pointer ${
              isDark ? 'text-zinc-400' : 'text-zinc-600'
            }`}>
              I agree to the{' '}
              <a href="#" className="text-red-600 hover:text-red-700 font-medium">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="#" className="text-red-600 hover:text-red-700 font-medium">
                Privacy Policy
              </a>
            </label>
          </div>

          {/* Sign Up Button */}
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-red-600 to-rose-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </span>
            ) : 'Create Account'}
          </button>
        </form>

        {/* Sign In Link */}
        <div className={`text-center text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onShowLogin}
            disabled={loading}
            className="text-red-600 hover:text-red-700 font-semibold transition-colors"
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignUpView;
